import React, { useEffect, useState } from 'react';
import { useUserStore } from '@core/user/useUserStore';
import { UserRole } from '@core/generated/gql-types';
import { useTenantId } from '@/contexts/TenantContext';
import { actionFocusRing2ClassName } from '@core/theme/actionTheme.util';

/**
 * Impersonate Component
 *
 * This component allows users with the appropriate permissions to impersonate another user.
 * It provides a form to input the user ID to impersonate and displays the current impersonation status.
 *
 * Features:
 * - Displays a form to set the impersonated user ID.
 * - Shows the current impersonation status if an impersonation is active.
 * - Allows editing or clearing the impersonation.
 *
 * Props: None
 *
 * State:
 * - `isShowing`: Controls the visibility of the impersonation form.
 * - `impersonated`: Stores the current impersonated user ID.
 */

// Switch to true when ready to use
const canImpersonate = false;
const Impersonate = () => {
  const {
    currentUser,
    setImpersonatedUser,
    impersonatedUserId,
    fetchCurrentUser,
  } = useUserStore();
  const [isShowing, setIsShowing] = useState(false);
  const [impersonated, setImpersonated] = useState(impersonatedUserId || '');
  const tenantId = useTenantId();
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (impersonated) {
      setImpersonatedUser(impersonated);
    }
    setIsShowing(false);
  };

  useEffect(() => {
    if (!impersonatedUserId) {
      return;
    }

    const refreshImpersonatedSession = async () => {
      await fetchCurrentUser();
    };

    void refreshImpersonatedSession();
  }, [fetchCurrentUser, impersonatedUserId]);

  if (
    canImpersonate &&
    (currentUser?.role === UserRole.HqAdmin || impersonated)
  ) {
    return (
      <div className="flex bg-yellow-200 p-2">
        <div className="flex flex-col align-middle">
          <div className="text-gray-500">Tenant ID: {tenantId}</div>
          {isShowing ? (
            <div>
              <form onSubmit={handleSubmit}>
                <input
                  className={`mt-1 mr-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 sm:text-sm ${actionFocusRing2ClassName}`}
                  value={impersonated}
                  placeholder="Enter user ID"
                  onChange={(e) => setImpersonated(e.target.value)}
                />
                <button
                  className="inline text-gray-500 underline"
                  type="submit"
                >
                  submit
                </button>
              </form>
              <button
                className="inline text-gray-500 underline"
                onClick={() => setIsShowing(false)}
              >
                Hide
              </button>
            </div>
          ) : (
            <>
              {impersonatedUserId ? (
                <div className="m-2 flex flex-row justify-center border-gray-200 align-middle">
                  <p className="text-gray-500">
                    Impersonating as <b>{impersonated}</b>
                  </p>
                  <button
                    className="ml-8 inline text-gray-500 underline"
                    onClick={() => setIsShowing(true)}
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div className="border-2 border-gray-200">
                  <button
                    className="inline text-gray-500 underline"
                    onClick={() => setIsShowing(true)}
                  >
                    impersonate
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default Impersonate;
